
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Supplier } from "@/types/cost";

export const useSuppliers = () => {
  return useQuery({
    queryKey: ['suppliers'],
    queryFn: async (): Promise<Supplier[]> => {
      console.log('🔄 Fetching suppliers from Supabase...');
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .order('name');
      
      if (error) {
        console.error('❌ Error fetching suppliers:', error);
        throw error;
      }
      console.log('✅ Suppliers fetched successfully:', data?.length || 0);
      return data || [];
    },
  });
};

export const useCreateSupplier = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (supplier: Omit<Supplier, 'id' | 'created_at' | 'updated_at'>) => {
      console.log('🔄 Creating supplier:', supplier);
      const { data, error } = await supabase
        .from('suppliers')
        .insert([supplier])
        .select()
        .single();
      
      if (error) {
        console.error('❌ Error creating supplier:', error);
        throw error;
      }
      console.log('✅ Supplier created successfully:', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast({
        title: "Success",
        description: "Supplier created successfully",
      });
    },
    onError: (error) => {
      console.error('❌ Supplier creation failed:', error);
      toast({
        title: "Error",
        description: "Failed to create supplier. Please check the console for details.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateSupplier = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Supplier> & { id: string }) => {
      console.log('🔄 Updating supplier:', id, updates);
      const { data, error } = await supabase
        .from('suppliers')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        console.error('❌ Error updating supplier:', error);
        throw error;
      }
      console.log('✅ Supplier updated successfully:', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast({
        title: "Success",
        description: "Supplier updated successfully",
      });
    },
    onError: (error) => {
      console.error('❌ Supplier update failed:', error);
      toast({
        title: "Error",
        description: "Failed to update supplier. Please check the console for details.",
        variant: "destructive",
      });
    },
  });
};
